import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '@/App.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page-container">
      <div className="legal-page-content">
        <Link to="/" className="legal-page-back-link">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        <h1 className="legal-page-title">
          Privacy <span className="gradient-text">Policy</span>
        </h1>

        <div className="legal-page-meta">
          <p>Last updated: January 2025</p>
        </div>

        <div className="legal-page-body">
          <section className="legal-section">
            <p className="legal-text">
              Thank you for choosing to be part of our community at GotoEcom - The Future of Fashion Commerce & Collaboration ("Company", "we", "us", or "our"). We are committed to protecting your personal information and your right to privacy.
            </p>
            <p className="legal-text">
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website GotoEcom - The Future of Fashion Commerce & Collaboration, use our mobile application, or interact with our services (collectively, the "Platform").
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">1. Information We Collect</h2>
            <p className="legal-text">We collect the following types of personal and usage data:</p>
            <h3 className="legal-subsection-title">a. Personal Information</h3>
            <ul className="legal-list">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Profile photo</li>
              <li>Social media account info (if login is via social platforms)</li>
              <li>Shipping/Billing address (if applicable)</li>
            </ul>

            <h3 className="legal-subsection-title">b. Usage Information</h3>
            <ul className="legal-list">
              <li>Device information (e.g., IP address, device type)</li>
              <li>Log and usage data</li>
              <li>Location data (if permitted)</li>
              <li>Browsing history within the app/website</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">2. How We Use Your Information</h2>
            <p className="legal-text">We use the collected information to:</p>
            <ul className="legal-list">
              <li>Provide and operate our Platform and services</li>
              <li>Create and manage user accounts</li>
              <li>Improve user experience and personalize content</li>
              <li>Provide customer support</li>
              <li>Monitor and analyze usage trends</li>
              <li>Prevent fraud, abuse, or illegal activities</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">3. Sharing Your Information</h2>
            <p className="legal-text">We may share your data:</p>
            <ul className="legal-list">
              <li>
                <strong>With vendors and service providers</strong> (e.g., hosting, payment gateways, analytics tools)
              </li>
              <li>
                <strong>With other users</strong> (e.g., when you post content, list items, or message others)
              </li>
              <li>
                <strong>For legal reasons</strong> (e.g., law enforcement, regulatory compliance)
              </li>
              <li>
                <strong>During business transfers</strong> (e.g., mergers, acquisitions)
              </li>
            </ul>
            <p className="legal-text legal-highlight">
              * We do <strong>not</strong> sell your personal data to third parties.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">4. Cookies and Tracking Technologies</h2>
            <p className="legal-text">
              We use cookies and similar technologies to collect and store information to enhance your experience. You can manage cookie preferences via your browser settings.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">5. Third-Party Services</h2>
            <p className="legal-text">
              Our Platform may contain links to third-party websites or services. We are not responsible for the privacy practices or content of those sites.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">6. Data Retention</h2>
            <p className="legal-text">
              We retain your data for as long as your account is active or as needed to fulfill legal, tax, or accounting obligations. You can request deletion of your account at any time.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">7. Security of Your Information</h2>
            <p className="legal-text">
              We implement reasonable administrative, technical, and physical security measures to protect your data. However, no system is completely secure.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">8. Your Privacy Rights</h2>
            <p className="legal-text">
              Depending on your location, you may have rights under applicable laws such as the GDPR or CCPA, including the right to:
            </p>
            <ul className="legal-list">
              <li>Access your personal data</li>
              <li>Request correction or deletion</li>
              <li>Object to processing</li>
              <li>Withdraw consent</li>
              <li>Data portability</li>
            </ul>
            <p className="legal-text">
              To exercise these rights, contact us at:{" "}
              <a href="mailto:hello@gotoecom.com" className="legal-link">hello@gotoecom.com</a>
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">9. Children's Privacy</h2>
            <p className="legal-text">
              Our Platform is not intended for children under the age of 13 (or under 16 in some jurisdictions). We do not knowingly collect data from minors.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">10. Updates to This Policy</h2>
            <p className="legal-text">
              We may update this Privacy Policy from time to time. The updated version will be posted on this page with a revised "Last Updated" date.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">11. Contact Us</h2>
            <p className="legal-text">
              If you have questions or concerns about this policy, contact us at:
            </p>
            <ul className="legal-list">
              <li>Email: <a href="mailto:hello@gotoecom.com" className="legal-link">hello@gotoecom.com</a></li>
              <li>Website: <a href="https://gotoecom.com" className="legal-link" target="_blank" rel="noopener noreferrer">GoToEcom - The Future of Fashion Commerce & Collaboration</a></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

